import { useCallback } from 'react';
import { SET_ITEM_BANNER } from '~/routes/remix-api.directory.set-item-banner';
import { isApError } from '~/modules/root/graphql/responses';
import { toast } from 'sonner';

/**
 * Feature flag gating the "set a project's banner from a tile slider image"
 * feature. Off by default (no published Feature row → evaluates off).
 */
export const PROJECT_TILE_BANNER_SELECTOR_FLAG = 'project_tile_banner_selector';

const DEFAULT_ERROR_MESSAGE =
    'Could not update the banner image. Please try again.';

type FlattenedErrorMessage = {
    formErrors?: unknown;
    fieldErrors?: unknown;
};

export interface SetItemBannerInput {
    libraryItemID: number;
    libraryImageID: number;
    /** Called if the mutation fails — use it to revert optimistic UI. */
    onError: () => void;
    /** Called when the banner is set successfully. */
    onSuccess?: () => void;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value) && typeof value === 'object';
}

function getStringMessages(value: unknown): string[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.filter(
        (message): message is string =>
            typeof message === 'string' && Boolean(message.trim()),
    );
}

/** Extracts a user-facing message from a remix-api error payload. */
function getSetItemBannerErrorMessage(data: unknown): string | null {
    if (!isRecord(data)) {
        return null;
    }

    if ('__ap_error' in data && 'message' in data) {
        const { message } = data as { message: unknown };
        if (typeof message === 'string' && message.trim()) {
            return message;
        }

        if (isRecord(message)) {
            const flattenedError = message as FlattenedErrorMessage;
            const formErrors = getStringMessages(
                flattenedError.formErrors,
            ).join(', ');
            const fieldErrors = Object.values(
                isRecord(flattenedError.fieldErrors)
                    ? flattenedError.fieldErrors
                    : {},
            )
                .flatMap(getStringMessages)
                .join(', ');
            const combined = [formErrors, fieldErrors]
                .filter(Boolean)
                .join('. ');
            return combined || null;
        }
    }

    if (
        'message' in data &&
        typeof data.message === 'string' &&
        data.message.trim()
    ) {
        return data.message;
    }

    return null;
}

/**
 * Sets a LibraryItem's top banner to a copy of one of its on-page images (via
 * the setItemBannerFromImage mutation). Returns a submit function that takes
 * per-call success/error callbacks so each tile can drive its own optimistic
 * state. Intended to be injected into the tile-actions context.
 *
 * Uses a plain `fetch` (not a shared `useFetcher`) so concurrent clicks on
 * different tiles each get an independent request and resolve their own
 * callbacks — a second click never supersedes the first.
 */
export function useSetItemBanner() {
    return useCallback(
        async ({
            libraryItemID,
            libraryImageID,
            onError,
            onSuccess,
        }: SetItemBannerInput) => {
            const fail = (message?: string) => {
                toast.error(message ?? DEFAULT_ERROR_MESSAGE);
                onError();
            };

            if (libraryItemID <= 0 || libraryImageID <= 0) {
                fail();
                return;
            }

            try {
                const formData = new FormData();
                formData.append('libraryItemID', String(libraryItemID));
                formData.append('libraryImageID', String(libraryImageID));

                const response = await fetch(SET_ITEM_BANNER, {
                    method: 'POST',
                    body: formData,
                });
                const data: unknown = await response.json().catch(() => null);

                if (!response.ok || isApError(data)) {
                    fail(getSetItemBannerErrorMessage(data) ?? undefined);
                    return;
                }

                const banner =
                    data &&
                    typeof data === 'object' &&
                    'setItemBannerFromImage' in data
                        ? (
                              data as {
                                  setItemBannerFromImage?: {
                                      BannerImage?: { ID?: number };
                                  };
                              }
                          ).setItemBannerFromImage
                        : null;

                if (banner?.BannerImage?.ID) {
                    toast.success('Banner image updated.');
                    onSuccess?.();
                } else {
                    fail();
                }
            } catch {
                fail();
            }
        },
        [],
    );
}
